
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, NavLink, Link, useNavigate, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Register from './components/Register';
import Logo from './components/Logo';
// Route-level code splitting (#15 / ADR-0004): defer the heaviest and least-
// common routes into their own chunks so the dashboard (the common landing
// path) doesn't ship them on first paint. `Admin` in particular pulls in
// `zod` (~303 KB) and `oboe`, which are admin-only.
const Admin = lazy(() => import('./components/Admin'));
const PlanningView = lazy(() => import('./components/PlanningView'));
const BasketComparison = lazy(() => import('./components/BasketComparison'));
const Profile = lazy(() => import('./components/Profile'));
const Accessibility = lazy(() => import('./components/Accessibility'));
const Impressum = lazy(() => import('./components/Impressum'));
const Datenschutz = lazy(() => import('./components/Datenschutz'));
// The cookie banner is an always-rendered overlay, but it alone pulls in
// `motion` (~120 KB). Deferring it keeps that off the first-paint payload; the
// banner mounts a beat after first render (acceptable for a consent overlay).
const CookieBanner = lazy(() => import('./components/CookieBanner'));
// Post-auth location overlays (Epic #20). Lazy so they stay off the first-paint
// payload for signed-out visitors.
const LocationOnboarding = lazy(() => import('./components/LocationOnboarding'));
const LocationRefreshBanner = lazy(() => import('./components/LocationRefreshBanner'));
import { User } from './shared/types';
import { useLanguage } from './services/LanguageContext';
import { authApi } from './services/auth';
import { auth } from './services/firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Shared focus-visible ring applied to every interactive nav element.
const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1';

const App: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const de = language === 'de';
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return saved === 'true';
    return typeof window !== 'undefined' && !!window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : true;
  });

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  // #23: the login refresh banner is dismissible for the session.
  const [locationBannerDismissed, setLocationBannerDismissed] = useState(false);
  // #153: "Später" dismisses the onboarding overlay for the session without
  // waiting on a network round-trip, so the flow never freezes.
  const [locationOnboardingDismissed, setLocationOnboardingDismissed] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try { setCurrentUser(await authApi.me()); }
        catch { setCurrentUser(null); }
      } else {
        setCurrentUser(null);
      }
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // Sync html[lang] with active language (SC 3.1.1).
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('darkMode') === null) setDarkMode(e.matches);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('darkMode', String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await authApi.logout();
    setCurrentUser(null);
    navigate('/login');
  };

  // Epic #20 overlays. Onboarding shows until the user makes a consent decision
  // (locationConsentGiven is undefined); the refresh banner shows when a
  // consented user is past the 30-day cooldown (#23) and hasn't dismissed it.
  const showLocationOnboarding = !!currentUser && currentUser.locationConsentGiven === undefined && !locationOnboardingDismissed;
  const showLocationBanner = !!currentUser
    && currentUser.locationConsentGiven === true
    && (currentUser as any).locationRefreshDue === true
    && !locationBannerDismissed
    && !showLocationOnboarding;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark">
        <div
          role="status"
          aria-label={t('a11y.loading')}
          className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"
        ></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-300 bg-white dark:bg-dark text-slate-900 dark:text-white">
      {/* Navigation Header */}
      <header className="border-b border-slate-200 dark:border-dark-border sticky top-0 z-50 bg-white/80 dark:bg-dark/80 backdrop-blur-md overflow-x-clip">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          {/* flex-wrap lets the row spill to a second line at ≤320 px (SC 1.4.10) */}
          <div className="flex justify-between items-center min-h-[5rem] py-2 flex-wrap gap-y-2">
            <div className="flex items-center">
              <Link
                to="/"
                className={`flex-shrink-0 flex items-center group rounded-sm ${focusRing}`}
                aria-label="Listago"
              >
                <Logo
                  textClassName="text-2xl"
                  showTagline
                  className="group-hover:scale-[1.02] transition-transform origin-left"
                  taglineClassName="hidden sm:block text-xs tracking-wide"
                />
              </Link>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="hidden lg:flex items-center space-x-1">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-xl text-sm font-bold transition-all ${focusRing} ${isActive ? 'text-emerald-500' : 'text-slate-500 hover:text-emerald-400'}`
                  }
                >
                  {t('nav.dashboard')}
                </NavLink>
              </div>

              {/* Dark mode toggle — SC 1.1.1, 2.4.7 */}
              <button
                onClick={toggleDarkMode}
                className={`w-10 h-10 flex items-center justify-center text-slate-500 dark:text-emerald-500/70 hover:text-emerald-500 hover:bg-emerald-500/5 rounded-xl transition-all ${focusRing}`}
                aria-label={darkMode ? t('a11y.switchToLight') : t('a11y.switchToDark')}
              >
                {darkMode
                  ? <i className="fas fa-moon" aria-hidden="true"></i>
                  : <i className="fas fa-sun" aria-hidden="true"></i>}
              </button>

              {/* Language switcher — SC 1.4.4, 2.5.3, 2.4.7 */}
              <div
                role="group"
                aria-label={t('a11y.selectLanguage')}
                className="flex items-center bg-slate-100 dark:bg-dark-border p-1 rounded-xl"
              >
                <button
                  onClick={() => setLanguage('de')}
                  aria-pressed={language === 'de'}
                  aria-label="DE – Deutsch"
                  className={`px-2 py-1 text-xs font-black rounded-lg transition-all ${focusRing} ${language === 'de' ? 'bg-emerald-500 text-dark shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:text-emerald-500'}`}
                >
                  DE
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  aria-pressed={language === 'en'}
                  aria-label="EN – English"
                  className={`px-2 py-1 text-xs font-black rounded-lg transition-all ${focusRing} ${language === 'en' ? 'bg-emerald-500 text-dark shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:text-emerald-500'}`}
                >
                  EN
                </button>
              </div>

              {currentUser ? (
                <div className="flex items-center gap-2 sm:gap-3">
                  {currentUser.role === 'admin' && (
                    <NavLink
                      to="/admin"
                      className={({ isActive }) =>
                        `px-3 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${focusRing} ${isActive ? 'text-emerald-500 bg-emerald-500/5' : 'text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/5'}`
                      }
                    >
                      <i className="fas fa-user-shield" aria-hidden="true"></i>
                      <span className="hidden sm:inline">{t('nav.admin')}</span>
                      <span className="sm:hidden sr-only">{t('nav.admin')}</span>
                    </NavLink>
                  )}
                  <NavLink
                    to="/profile"
                    className={`flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border border-emerald-500/20 rounded-xl font-bold text-sm hover:bg-emerald-500 hover:text-white transition-all shadow-sm ${focusRing}`}
                  >
                    <i className="fas fa-user-circle text-xs" aria-hidden="true"></i>
                    <span className="hidden md:inline">{currentUser.email.split('@')[0]}</span>
                    <span className="md:hidden sr-only">{currentUser.email.split('@')[0]}</span>
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className={`px-3 py-2.5 bg-slate-100 dark:bg-dark-border text-slate-500 dark:text-emerald-500/70 font-bold text-sm rounded-xl hover:bg-red-500 hover:text-white transition-all ${focusRing}`}
                    aria-label={t('nav.logout')}
                  >
                    <i className="fas fa-power-off" aria-hidden="true"></i>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className={`px-5 py-2.5 bg-emerald-500 text-dark font-bold text-sm rounded-xl hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/20 ${focusRing}`}
                >
                  {t('nav.login')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* #23: login-time "still shopping near …?" banner. */}
      {showLocationBanner && currentUser && (
        <Suspense fallback={null}>
          <LocationRefreshBanner
            user={currentUser}
            onUpdated={setCurrentUser}
            onDismiss={() => setLocationBannerDismissed(true)}
          />
        </Suspense>
      )}

      {/* Main Content */}
      <main className="flex-grow">
        {/* Suspense boundary for the lazily-loaded routes (#15). Shows a spinner
            while a route chunk is fetched on navigation. */}
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-24">
              <div
                role="status"
                aria-label={t('a11y.loading')}
                className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full motion-safe:animate-spin"
              ></div>
            </div>
          }
        >
          <Routes>
            <Route path="/login" element={<Login onLogin={setCurrentUser} />} />
            <Route path="/register" element={<Register onLogin={setCurrentUser} />} />
            <Route path="/" element={<Dashboard currentUser={currentUser} onUpdateUser={setCurrentUser} />} />
            <Route path="/profile" element={currentUser ? <Profile currentUser={currentUser} onUpdateUser={setCurrentUser} /> : <Navigate to="/login" />} />
            <Route path="/admin" element={currentUser?.role === 'admin' ? <Admin /> : <Navigate to="/" />} />
            <Route path="/plan/:id" element={currentUser ? <PlanningView /> : <Navigate to="/login" />} />
            <Route path="/compare/:listId" element={currentUser ? <BasketComparison /> : <Navigate to="/login" />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
          </Routes>
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 dark:bg-dark border-t border-slate-200 dark:border-dark-border py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex justify-center items-center mb-6">
            <Logo textClassName="text-xl" showTagline taglineClassName="text-xs tracking-wide" className="items-center text-center" />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
            {t('footer.copy')}
          </p>
          <nav aria-label={de ? 'Rechtliche Hinweise' : 'Legal'} className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-1">
            <Link to="/impressum"    className={`text-sm text-emerald-600 hover:text-emerald-500 font-bold ${focusRing} rounded`}>{t('nav.imprint')}</Link>
            <Link to="/datenschutz"  className={`text-sm text-emerald-600 hover:text-emerald-500 font-bold ${focusRing} rounded`}>{t('nav.privacy')}</Link>
            <Link to="/accessibility" className={`text-sm text-emerald-600 hover:text-emerald-500 font-bold ${focusRing} rounded`}>{t('nav.accessibility')}</Link>
          </nav>
        </div>
      </footer>
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>

      {/* #21/#25: transparency-first location onboarding overlay. */}
      {showLocationOnboarding && (
        <Suspense fallback={null}>
          <LocationOnboarding onComplete={setCurrentUser} onDismiss={() => setLocationOnboardingDismissed(true)} />
        </Suspense>
      )}
    </div>
  );
};

export default App;
